// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';

// Internal Dependencies
import { updateModalContent, toggleModal } from '../../../actions/modal';
import OverLoading from '../../global/OverLoading';
import MText from '../../common/MText';
import MButton from '../../common/MButton';
import Colours from '../../../styles/colours';
import apiService from '../../../services.js/apiService';
import { IsArray, IsEmpty } from '../../../common/checks';
import useDebounce from '../../global/use-debounce';

const PopularTagsTable = (props) => {
  const history = useHistory();
  const tableRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  const debouncedSearch = useDebounce(inputValue, 400);

  const fetchPopularTags = async () => {
    try {
      setLoading(true);
      const result = await apiService.getPopularTags();
      const tags = IsArray(result) ? result : [];
      setTableData(tags.map((t) => ({ name: t })));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopularTags();
  }, []);

  useEffect(() => {
    if (IsEmpty(debouncedSearch) || IsEmpty(debouncedSearch.trim())) {
      setSearchResults([]);
      return;
    }

    const fetchTags = async () => {
      try {
        setSearchLoading(true);
        // exclude already added ones
        const result = await apiService.getTags({
          name:     debouncedSearch,
          pageNo:   0,
          pageSize: 50,
        });
        const fetchedTags = IsArray(result && result.rows) ? result.rows : [];
        const filtered = fetchedTags.filter((t) => !tableData.some((d) => d.name === t.name));
        const sorted = filtered.sort((a, b) => {
          if (a.isApproved === b.isApproved) return 0;
          return a.isApproved ? -1 : 1;
        });
        setSearchResults(sorted);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchTags();
  }, [debouncedSearch, tableData]);

  const addTagToTable = async (tag) => {
    try {
      if (tableData.some((t) => t.name === tag.name)) {
        toast.info('Tag already added to popular tags');
        return;
      }

      setLoading(true);

      const newTags = [...tableData, { name: tag.name }];
      await apiService.updatePopularTags(newTags.map((t) => t.name));

      setTableData(newTags);
      toast.success(`Tag "${tag.name}" added to popular tags`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tag) => {
    props.updateModalContent({
      title: (
        <MText text="Remove Confirmation" size={16} bold />
      ),
      content: (
        <MText
          text={(
            <>
              Remove
              {' '}
              <strong>{tag.name}</strong>
              {' '}
              from popular tags?
            </>
          )}
          size={14}
        />
      ),
      cancelText:   'Cancel',
      actionButton: {
        actionText: 'Remove',
        actionType: 'danger',
        action:     async () => {
          try {
            setLoading(true);
            const newTags = tableData.filter((t) => t.name !== tag.name);
            await apiService.updatePopularTags(newTags.map((t) => t.name));

            toast.success(`Tag "${tag.name}" removed from popular tags`);
            setTableData(newTags);
          } catch (err) {
            toast.error(`Failed to remove tag: ${err.message}`);
          } finally {
            setLoading(false);
          }
        },
      },
      centered:         true,
      closeAfterAction: true,
    });
    props.toggleModal();
  };

  const columns = [
    {
      title:  'No.',
      field:  'index',
      render: (r) => (
        <MText text={r.tableData.id + 1} />
      ),
      cellStyle: {
        textAlign: 'center',
        width:     '15%',
      },
      headerStyle: {
        textAlign: 'center',
        width:     '15%',
      },
    },
    {
      title:  'Tag',
      field:  'name',
      render: (r) => (
        <div
          style={{
            display:         'inline-block',
            borderRadius:    3,
            padding:         '6px 10px',
            backgroundColor: r.isApproved === false ? Colours.Red : Colours.Green1,
          }}
        >
          <MText text={r.name} style={{ color: Colours.White }} bold />
        </div>
      ),
    },
  ];

  return (
    <div>
      {loading && <OverLoading />}
      <div
        className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-3"
        style={{ gap: '10px' }}
      >
        <div className="w-100" style={{ maxWidth: '50%' }}>
          <FormControl style={{ width: '100%' }}>
            <Autocomplete
              size="small"
              options={searchResults}
              getOptionLabel={(option) => option.name || ''}
              getOptionDisabled={(option) => !option.isApproved}
              noOptionsText={IsEmpty(debouncedSearch) ? '' : 'No result found'}
              inputValue={inputValue}
              popupIcon={
                searchLoading ? <CircularProgress color="inherit" size={16} /> : null
              }
              loading={searchLoading}
              open={open && Boolean(debouncedSearch.trim())}
              onOpen={() => {
                if (debouncedSearch.trim()) {
                  setOpen(true);
                }
              }}
              onClose={(_, reason) => {
                if (reason !== 'select-option') {
                  setOpen(false);
                  setInputValue('');
                }
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'reset') {
                  return;
                }
                setInputValue(value);
                if (reason === 'input') {
                  setOpen(Boolean(value.trim()));
                }
              }}
              onChange={(_, value) => {
                if (!IsEmpty(value) && !value.isApproved) {
                  toast.info('Cannot add unapproved tag');
                  return;
                }
                if (!IsEmpty(value)) {
                  addTagToTable(value);
                }
              }}
              renderOption={(option) => (
                <div className="w-100 py-2 px-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div
                      style={{
                        display:         'inline-block',
                        borderRadius:    3,
                        padding:         '4px 10px',
                        backgroundColor: option.isApproved ? Colours.Green1 : Colours.Red,
                      }}
                    >
                      <MText text={option.name} bold style={{ color: Colours.White }} />
                    </div>

                    {option.isApproved && (
                      <MButton
                        text="+"
                        variant="contained"
                        colour={Colours.White}
                        textColour={Colours.Black}
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          border:         `1px solid ${Colours.Gray2}`,
                          borderRadius:   '50%',
                          minWidth:       26,
                          minHeight:      26,
                          padding:        0,
                          fontSize:       16,
                          lineHeight:     '16px',
                          display:        'flex',
                          alignItems:     'center',
                          justifyContent: 'center',
                        }}
                        disabled
                      />
                    )}
                  </div>
                </div>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search tags..."
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: 13, padding: '3px 5px' },
                  }}
                />
              )}
            />
          </FormControl>
        </div>

        <div className="d-flex justify-content-end w-100" style={{ maxWidth: '50%' }}>
          <MButton
            text="Manage Group"
            variant="contained"
            colour={Colours.MaterialBlue}
            textColour={Colours.White}
            onClick={() => history.push('/admin/tags/popular/groups')}
          />
        </div>
      </div>

      <MaterialTable
        tableRef={tableRef}
        className="material-table"
        title=""
        columns={columns}
        data={tableData}
        isLoading={loading}
        actions={[
          {
            icon:    'delete',
            tooltip: 'Delete Tag',
            onClick: (_, tag) => handleDelete(tag),
          },
        ]}
        options={{
          paging:              true,
          pageSize:            20,
          pageSizeOptions:     [20, 50, 100],
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              true,
          sorting:             false,
          draggable:           false,
        }}
      />
    </div>
  );
};

PopularTagsTable.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, { updateModalContent, toggleModal })(PopularTagsTable);
